// The bell (30-day list row 19): every alert sent to this person, grouped by project with the
// unread first; tap one and it opens the thing it is about and is marked read.
import { useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useAuth } from '../auth';
import { Icon } from '../app/Icon';
import { Head, Empty } from '../app/page';
import { useProjects } from '../app/projects';
import { useAlerts, markRead, groupAlerts, kindWord, type Alert } from '../app/alerts';
import { fmtWhen } from '../app/fmt';
import { toast } from '../app/toast';

export function AlertsScreen() {
  const { session } = useAuth();
  const nav = useNavigate();
  const list = useAlerts();
  const projects = useProjects({ all: true });
  const nameOf = useMemo(() => { const m = new Map((projects.data ?? []).map(p => [p.id, p.name])); return (pid: string | null) => pid ? (m.get(pid) ?? 'Other projects') : 'TradeView'; }, [projects.data]);
  const groups = useMemo(() => groupAlerts(list, nameOf), [list, nameOf]);
  const unread = list.filter(a => !a.read);
  async function open(a: Alert) {
    if (session && !a.read) { try { await markRead(session, [a.id]); } catch { /* the badge catches up */ } }
    if (a.link) nav(a.link); else toast('This one has nothing to open.');
  }
  async function readAll() {
    if (!session || !unread.length) return;
    try { await markRead(session, unread.map(a => a.id)); toast(`${unread.length} marked read`); } catch (e) { toast(`Couldn't mark them: ${e instanceof Error ? e.message : e}`, 'crit'); }
  }
  return (
    <div className="flex flex-col gap-2.5 pb-24">
      <Head title="Alerts" sub={unread.length ? `${unread.length} unread` : 'All read'} right={unread.length > 0 ? <button className="btn btn-sm" onClick={readAll}><Icon name="check" size={16} />Mark all read</button> : undefined} />
      {list.length === 0 && <Empty icon="bell" title="Nothing yet" text="When something is assigned to you, sent, approved or running late, it lands here and on your phone." />}
      {groups.map(g => (
        <section key={g.pid ?? ''} className="card py-1">
          <h4 className="lbl m-0 mt-1.5 mb-1 flex items-center justify-between gap-2"><span className="truncate">{g.project}</span>{g.unread > 0 && <span className="pill pill-nodot pill-crit shrink-0">{g.unread} new</span>}</h4>
          {g.items.map(a => (
            <button key={a.id} onClick={() => open(a)} className="w-full text-left flex items-start gap-2.5 py-2 border-t border-line first:border-0 min-w-0">
              <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${a.read ? 'bg-transparent' : 'bg-accent'}`} />
              <span className="min-w-0 flex-1">
                <span className="flex items-center justify-between gap-2"><span className={`text-[11px] font-head font-bold uppercase tracking-wide ${a.read ? 'text-muted' : 'text-accent'}`}>{kindWord(a.kind)}</span><span className="text-muted text-[11.5px] shrink-0">{a.at ? fmtWhen(a.at) : ''}</span></span>
                <b className={`block text-[13.5px] truncate ${a.read ? 'font-medium' : 'font-semibold'}`}>{a.itemLabel}</b>
                {a.text && <span className="text-muted text-[12.5px] block">{a.text}</span>}
              </span>
            </button>
          ))}
        </section>
      ))}
    </div>
  );
}
